import { LoadingState } from '@/components/LoadingState';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireAdmin } from '@/components/RequireRole';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { MainLayout } from '@/layouts/MainLayout';
import { ProjectLayout } from '@/layouts/ProjectLayout';
import { useAuthStore } from '@/stores/auth';
import { Suspense, lazy } from 'react';
import { type Location, Navigate, Route, Routes, useLocation } from 'react-router-dom';

const LoginPage = lazy(() => import('@/pages/LoginPage'));
const WelcomePage = lazy(() => import('@/pages/WelcomePage'));
const PendingPage = lazy(() => import('@/pages/PendingPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const EventListPage = lazy(() => import('@/pages/EventListPage'));
const EventDetailPage = lazy(() => import('@/pages/EventDetailPage'));
const CaseListPage = lazy(() => import('@/pages/CaseListPage'));
const CaseDetailPage = lazy(() => import('@/pages/CaseDetailPage'));
const OverviewPage = lazy(() => import('@/pages/OverviewPage'));
const CommonDefectsPage = lazy(() => import('@/pages/CommonDefectsPage'));
const CommonDefectDetailPage = lazy(() => import('@/pages/CommonDefectDetailPage'));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'));
const SubmitPage = lazy(() => import('@/pages/SubmitPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const DeviceDetailPage = lazy(() => import('@/pages/DeviceDetailPage'));
const ProjectOverviewPage = lazy(() => import('@/pages/ProjectOverviewPage'));
const ProjectDevicesPage = lazy(() => import('@/pages/ProjectDevicesPage'));
const ProjectCatalogPage = lazy(() => import('@/pages/ProjectCatalogPage'));
const ProjectMembersPage = lazy(() => import('@/pages/ProjectMembersPage'));
const MemberGroupsPage = lazy(() => import('@/pages/MemberGroupsPage'));
const ProjectSettingsPage = lazy(() => import('@/pages/ProjectSettingsPage'));
const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const AdminCompaniesPage = lazy(() => import('@/pages/AdminCompaniesPage'));
const AdminWorkersPage = lazy(() => import('@/pages/AdminWorkersPage'));
const AdminRelaysPage = lazy(() => import('@/pages/AdminRelaysPage'));
const AdminPiCredentialsPage = lazy(() => import('@/pages/AdminPiCredentialsPage'));
const AdminMailSettingsPage = lazy(() => import('@/pages/AdminMailSettingsPage'));
const AdminAuditLogsPage = lazy(() => import('@/pages/AdminAuditLogsPage'));
const AdminQueueHealthPage = lazy(() => import('@/pages/AdminQueueHealthPage'));
const AdminOidcProvidersPage = lazy(() => import('@/pages/AdminOidcProvidersPage'));
const AdminReleasesPage = lazy(() => import('@/pages/AdminReleasesPage'));
const AdminReleaseNewPage = lazy(() => import('@/pages/AdminReleaseNewPage'));
const AdminReleaseEditPage = lazy(() => import('@/pages/AdminReleaseEditPage'));
const SharePage = lazy(() => import('@/pages/SharePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const PublicLayout = lazy(() => import('@/layouts/PublicLayout'));
const DocsLayout = lazy(() => import('@/layouts/DocsLayout'));
const DocsPage = lazy(() => import('@/pages/DocsPage'));
const DownloadPage = lazy(() => import('@/pages/DownloadPage'));

// `/` is the front door: signed-in users go straight to the console dashboard —
// every project they can see, in one page (spec §27) — and everyone else gets
// the landing page (which leads to /login).
function RootRedirect() {
  const access = useAuthStore((s) => s.access);
  return <Navigate to={access ? '/dashboard' : '/welcome'} replace />;
}

/**
 * The pages that open as a panel over the shell rather than replacing it, and
 * what to draw behind each one.
 *
 * A panel is not a page: it is drawn over the page that owns its trigger, so
 * `Routes` matches on `shellLocation(location)` while every hook inside the
 * panel still reads the real one (see the second `<Routes>` below). A deep link
 * — the URL typed, or the page reloaded — has no page behind it, so the list
 * that owns the record is drawn in its place.
 *
 * `/submit` keeps the list's own `?project=`/`?q=`: the panel opened from a
 * filtered list should come back to that list. The project panels are opened
 * from the projects list, which has no query worth carrying.
 */
const PROJECT_PANEL_RE = /^\/projects\/[^/]+\/(?:members|settings)$/;
const RECORD_DETAIL_RE = /^\/(events|cases|common-defects)\/[^/]+$/;

/**
 * The page a panel was opened from, if the link that opened it said so.
 *
 * A record is always read in a right-hand sheet. The row's link hands over where
 * it stood (`state.background`), and `shellLocation` supplies the matching list
 * when the URL was typed or reloaded.
 */
export function backgroundOf(location: Location): Location | null {
  const state = location.state as { background?: Location } | null;
  return state?.background ?? null;
}

export function shellLocation(location: Location): Partial<Location> {
  if (location.pathname === '/submit') {
    return { pathname: '/events', search: location.search, hash: '', state: null };
  }
  if (PROJECT_PANEL_RE.test(location.pathname)) {
    return { pathname: '/projects', search: '', hash: '', state: null };
  }
  const record = location.pathname.match(RECORD_DETAIL_RE);
  if (record) {
    return { pathname: `/${record[1]}`, search: location.search, hash: '', state: null };
  }
  return location;
}

export function AppRoutes() {
  const location = useLocation();
  const background = backgroundOf(location);
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes location={background ?? shellLocation(location)}>
        <Route path="/" element={<RootRedirect />} />

        {/* One shell, two sides (spec §34). `/login` puts the form first and the
            product plane on the right; `/pending` is the end of the same flow
            and mirrors it, so the two states are told apart at a glance. */}
        <Route element={<AuthLayout panelSide="right" />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        <Route element={<AuthLayout panelSide="left" />}>
          <Route path="/pending" element={<PendingPage />} />
        </Route>

        <Route path="/share/:token" element={<SharePage />} />

        <Route element={<PublicLayout />}>
          <Route path="/welcome" element={<WelcomePage />} />
        </Route>

        {/* The manual is a page of its own, in a shell of its own. It used to
            borrow the reader's session shell — public for anonymous, console
            for signed-in (spec §30) — which made it read as a tab of `/welcome`
            when a reader arrived from the landing page. `DocsLayout` is now the
            only frame it renders in, for everyone. See spec §39. */}
        <Route element={<DocsLayout />}>
          <Route path="/docs" element={<DocsPage />} />
        </Route>

        <Route
          element={
            <RequireAuth>
              <RouteErrorBoundary>
                <MainLayout />
              </RouteErrorBoundary>
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<OverviewPage />} />
          <Route path="/common-defects" element={<CommonDefectsPage />} />
          <Route path="/common-defects/:signature" element={<CommonDefectDetailPage />} />
          {/* The landing page shipped as `/overview` first; that URL is in
              bookmarks, so forward it rather than 404 it. */}
          <Route path="/overview" element={<Navigate to="/dashboard" replace />} />
          <Route path="/events" element={<EventListPage />} />
          <Route path="/cases" element={<CaseListPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/account" element={<AccountPage />} />
          {/* The console's own appearance — the rail's last row (§32). Beside
              /account, not inside it: this is about the console, that is about
              the person. */}
          <Route path="/settings" element={<SettingsPage />} />
          {/* Behind RequireAuth: the binaries carry the collectors, so the
              page and the signed URLs it mints are for signed-in users. */}
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/devices/:id" element={<DeviceDetailPage />} />

          <Route path="/projects/:id" element={<ProjectLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<ProjectOverviewPage />} />
            <Route path="overview" element={<Navigate to="../dashboard" replace />} />
            <Route path="devices" element={<ProjectDevicesPage />} />
            <Route path="catalog" element={<ProjectCatalogPage />} />
          </Route>
        </Route>

        <Route
          element={
            <RequireAuth>
              <RequireAdmin>
                <RouteErrorBoundary>
                  <AdminLayout />
                </RouteErrorBoundary>
              </RequireAdmin>
            </RequireAuth>
          }
        >
          <Route path="/admin" element={<Navigate to="users" replace />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/member-groups" element={<MemberGroupsPage />} />
          <Route path="/admin/companies" element={<AdminCompaniesPage />} />
          <Route path="/admin/workers" element={<AdminWorkersPage />} />
          <Route path="/admin/relays" element={<AdminRelaysPage />} />
          <Route path="/admin/pi-credentials" element={<AdminPiCredentialsPage />} />
          <Route path="/admin/mail" element={<AdminMailSettingsPage />} />
          <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
          <Route path="/admin/queue-health" element={<AdminQueueHealthPage />} />
          <Route path="/admin/oidc-providers" element={<AdminOidcProvidersPage />} />
          <Route path="/admin/releases" element={<AdminReleasesPage />} />
          <Route path="/admin/releases/new" element={<AdminReleaseNewPage />} />
          <Route path="/admin/releases/:id/edit" element={<AdminReleaseEditPage />} />
        </Route>

        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Panels that open over the shell instead of replacing it. They sit
          outside the layout routes because they are not pages: their sheet
          portals to the body and the page above keeps rendering. */}
      <Suspense fallback={null}>
        <Routes>
          {/* Event, case and pattern details share one right-hand sheet frame.
              The list behind them comes from `state.background` when a row
              opened them, or from `shellLocation` on a deep link. */}
          <Route
            path="/events/:id"
            element={
              <RequireAuth>
                <EventDetailPage panel />
              </RequireAuth>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <RequireAuth>
                <CaseDetailPage panel />
              </RequireAuth>
            }
          />
          <Route
            path="/common-defects/:signature"
            element={
              <RequireAuth>
                <CommonDefectDetailPage />
              </RequireAuth>
            }
          />
          {background && (
            <>
              {/* The machines are the third record type that opens this way
                  (§61): a device's own page is `/devices/:id`, and the devices
                  table's row opens that same record over the list it is in. */}
              <Route
                path="/devices/:id"
                element={
                  <RequireAuth>
                    <DeviceDetailPage panel />
                  </RequireAuth>
                }
              />
            </>
          )}
          <Route
            path="/submit"
            element={
              <RequireAuth>
                <SubmitPage />
              </RequireAuth>
            }
          />
          {/* The project's members and settings used to be rail rows. They are
              panels now, opened from the projects list's row menu (§29). */}
          <Route
            path="/projects/:id/members"
            element={
              <RequireAuth>
                <ProjectMembersPage />
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:id/settings"
            element={
              <RequireAuth>
                <ProjectSettingsPage />
              </RequireAuth>
            }
          />
          {/* Matches nothing by design: without it React Router warns on every
              navigation that this second table has no route for the page you
              are actually on. */}
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    </Suspense>
  );
}
